/**
 * Node modules
 */
import axios from 'axios';

/**
 * Configurations
 */
import { app } from '../../../config';

/**
 * Offline resilience: every successful GET is cached in localStorage; when a
 * backend is down or hogged the last-good response is served instead of
 * failing the page (stale-if-error). Bet entry must survive outages of the
 * read-side services — served fallbacks carry `metadata.isStale = true`.
 */
// Bump OFFLINE_CACHE_SCHEMA_VERSION whenever API payload shapes change so
// stale-shaped cache entries from older builds are never served to new code.
const OFFLINE_CACHE_SCHEMA_VERSION = 'v1';
const OFFLINE_CACHE_PREFIX = `${app.SITE_ID}#offline-cache#${OFFLINE_CACHE_SCHEMA_VERSION}#`;
const OFFLINE_CACHE_MAX_ENTRIES = 300;
const OFFLINE_CACHE_MAX_ENTRY_BYTES = 400 * 1024;
// Timeout ladder — every tier must stay UNDER the gateway's outbound timeout
// (320s, HTTP_CLIENT_TIMEOUT_MS in the ecosystem config):
//   default 4 min: pages paint instantly from the offline cache regardless,
//   so this only bounds how long a network attempt may run before erroring
//   (and falling back to cache). Dead-backend detection is slower at 4 min —
//   accepted trade for long-running admin/report routes.
//   long 5 min: known multi-minute endpoints.
const REQUEST_TIMEOUT_IN_MILLISECONDS = 4 * 60 * 1000;
const LONG_REQUEST_TIMEOUT_IN_MILLISECONDS = 5 * 60 * 1000;
const LONG_REQUEST_URL_MARKERS = ['/check-cache/', '/deal-receipts', '/get-track-sales-data'];
const resolveTimeout = url => (LONG_REQUEST_URL_MARKERS.some(marker => String(url).includes(marker))
  ? LONG_REQUEST_TIMEOUT_IN_MILLISECONDS
  : REQUEST_TIMEOUT_IN_MILLISECONDS);

// socketId is per-session noise — excluding it lets caches hit across visits.
const buildCacheKey = (url, query) => {
  const stable = { ...(query || {}) };
  delete stable.socketId;
  return `${OFFLINE_CACHE_PREFIX}${url}?${JSON.stringify(stable)}`;
};

const resolveUrl = (url, params) => {
  if (!params) {
    return url;
  }
  const link = new URL(url);
  const links = [];
  link.pathname.split('/').forEach((chunk) => {
    if (chunk.startsWith(':')) {
      const key = chunk.replace(/:/g, '');
      if (params[key]) {
        links.push(params[key]);
      }
    } else {
      links.push(chunk);
    }
  });
  link.pathname = links.join('/');
  return link.href.replace(/\?/g, '');
};

/**
 * Synchronous last-good lookup for stale-while-revalidate: pages render from
 * this instantly while the network refresh happens behind.
 */
export const peekOfflineCache = (input) => {
  const {
    method,
    params,
    query,
    url,
  } = input;
  if (String(method).toUpperCase() !== 'GET') {
    return null;
  }
  return readOfflineCache(buildCacheKey(resolveUrl(url, params), query));
};

const readOfflineCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeOfflineCache = (key, value) => {
  try {
    const raw = JSON.stringify(value);
    if (raw.length > OFFLINE_CACHE_MAX_ENTRY_BYTES) {
      return;
    }
    const ours = Object.keys(localStorage).filter(k => k.startsWith(OFFLINE_CACHE_PREFIX));
    if (ours.length >= OFFLINE_CACHE_MAX_ENTRIES) {
      // evict oldest tenth to stay bounded
      ours
        .map(k => ({ key: k, savedAt: (readOfflineCache(k) || {}).savedAt || 0 }))
        .sort((a, b) => a.savedAt - b.savedAt)
        .slice(0, Math.ceil(OFFLINE_CACHE_MAX_ENTRIES / 10))
        .forEach(entry => localStorage.removeItem(entry.key));
    }
    localStorage.setItem(key, raw);
  } catch (e) {
    // Quota exceeded: drop our own cache entries and retry once.
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(OFFLINE_CACHE_PREFIX))
        .forEach(k => localStorage.removeItem(k));
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e2) {
      // Give up silently — caching is best-effort.
    }
  }
};

// Server-side failures fall back to cache; business errors (validation, no
// access) and session expiry must still surface.
const isFallbackWorthy = (error) => {
  if (!error || !error.message) {
    return true;
  }
  if (error.message.includes('invalid-account-session')) {
    return false;
  }
  if (!error.metadata) {
    return true; // network error, timeout, or non-API response (dead gateway)
  }
  return error.message.includes('something-went-wrong')
    || error.message.includes('connection-problem');
};

export default async (input) => {
  const {
    body,
    method,
    params,
    query,
    url,
  } = input;
  const isGet = String(method).toUpperCase() === 'GET';
  let cacheKey = null;
  try {
    const token = localStorage.getItem(`${app.SITE_ID}#token`);
    const requesting = {
      headers: {
        'Accept': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json; charset=utf-8',
      },
      method,
      timeout: resolveTimeout(url),
      url,
      validateStatus: null,
      withCredentials: true,
    };

    requesting.url = resolveUrl(url, params);
    requesting.params = query;
    requesting.data = body;
    cacheKey = isGet ? buildCacheKey(requesting.url, query) : null;
    const t0 = new Date().valueOf();
    const {
      data: payload,
      headers,
    } = await axios(requesting);
    if (url.includes('api/accounts/account/:accountId/permissions')) {
      const t3 = new Date().valueOf();
      const t1 = new Date(Number(headers['private-request-time'])).valueOf();
      const t2 = new Date(Number(headers['private-response-time'])).valueOf();
      const offset = ((t1 - t0) - (t2 - t3)) / 2;
      if (!Number.isNaN(offset)) {
        localStorage.setItem(`${app.SITE_ID}#server-date`, String(t2));
        localStorage.setItem(`${app.SITE_ID}#time-offset`, String(((t1 - t0) - (t2 - t3)) / 2));
      }
    }
    const metadata = JSON.parse(headers['private-metadata']);
    delete headers['private-metadata'];
    delete headers['private-request-time'];
    delete headers['private-response-time'];
    const {
      message,
      status,
    } = metadata;
    if (status === 'error') {
      if (message.includes('invalid-account-session')) {
        window.location.reload();
      }
      const err = new Error(message);
      err.metadata = metadata;
      throw err;
    } else if (status === 'success') {
      if (cacheKey) {
        writeOfflineCache(cacheKey, {
          headers,
          metadata,
          payload,
          savedAt: Date.now(),
        });
      }
      return {
        headers,
        metadata,
        payload,
      };
    } else {
      throw new Error('...');
    }
  } catch (e) {
    if (cacheKey && isFallbackWorthy(e)) {
      const cached = readOfflineCache(cacheKey);
      if (cached) {
        return {
          headers: cached.headers || {},
          metadata: {
            ...cached.metadata,
            isStale: true,
            staleSince: cached.savedAt,
          },
          payload: cached.payload,
        };
      }
    }
    if (!e.metadata) {
      // CORS/network failures, timeouts, and non-API responses carry no
      // metadata. Normalize them so downstream handlers (feedback toasts,
      // formik setSubmitting resets) always receive a well-formed error —
      // otherwise submit buttons spin forever on connection problems.
      const err = new Error('message.error.connection-problem-occurred');
      err.metadata = {
        message: err.message,
        status: 'error',
      };
      throw err;
    }
    throw e;
  }
};
